import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';

// MUI Icons
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface AceTableExportModalProps {
  open: boolean;
  handleClose: () => void;
  // Triggers the actual fetch + CSV build in the parent. Receives the user's
  // chosen file name (without extension); empty string means "use the default".
  // Async so the modal can keep its spinner up until the download is ready.
  onExport: (fileName: string) => Promise<void> | void;
  isExporting: boolean;
  // Total rows matching the CURRENT view (active tab + filters + search).
  // This is the same `total` the table's paginator uses, so the count the
  // user sees here is exactly what will be exported.
  totalRows: number;
  rowLimit: number;
  activeTab: string;
  // Whether any tariff / finding / country filter or search is active. Drives
  // the copy that nudges the user toward narrowing when they're over the cap.
  hasActiveFilters: boolean;
  // Default file name (without the .csv extension) shown as the input's
  // placeholder and used when the user leaves the field blank.
  defaultFileName: string;
}

const AceTableExportModal: React.FC<AceTableExportModalProps> = ({
  open,
  handleClose,
  onExport,
  isExporting,
  totalRows,
  rowLimit,
  activeTab,
  hasActiveFilters,
  defaultFileName,
}) => {
  const [fileName, setFileName] = useState('');

  if (!open) return null;

  const overLimit = totalRows > rowLimit;
  const noRows = totalRows === 0;
  const canExport = !overLimit && !noRows && !isExporting;

  // Shared row layout for the view-summary lines. The explicit gap guarantees
  // space between label and value even when the label is wide enough to leave
  // space-between with ~0px of slack (otherwise the count sits flush against
  // "Rows matching this view").
  const summaryRowStyle: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '16px',
  };

  const handleExportClick = () => {
    if (canExport) onExport(fileName.trim());
  };

  return (
    <div
      className='modal-overlay'
      onClick={() => !isExporting && handleClose()}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        className='modal-cont'
        style={{
          width: '560px',
          maxWidth: '90vw',
          padding: 0,
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='modal-header-cont-temp'
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--es-soft-border)',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <div className='modal-header-cont-text'>
              <h1
                className='modal-header-cont-text-h1'
                style={{
                  fontSize: '20px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <IosShareIcon style={{ color: '#4895ef' }} /> Export Current
                View (CSV)
              </h1>
              <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
                Downloads exactly what the table is showing &mdash; your active
                tab, filters, and search are all applied.
              </p>
            </div>
            <div
              className='module-header-btn-close'
              onClick={() => !isExporting && handleClose()}
              style={{
                cursor: isExporting ? 'not-allowed' : 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className='modal-body'
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* View summary */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              fontSize: '14px',
              color: 'var(--text-primary-color)',
            }}
          >
            <div style={summaryRowStyle}>
              <span style={{ color: 'var(--text-secondary)' }}>Tab</span>
              <strong>{activeTab}</strong>
            </div>
            <div style={summaryRowStyle}>
              <span style={{ color: 'var(--text-secondary)' }}>Filters</span>
              <strong>{hasActiveFilters ? 'Applied' : 'None'}</strong>
            </div>
            <div style={summaryRowStyle}>
              <span
                style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
              >
                Rows matching this view
              </span>
              <strong style={{ color: overLimit ? '#f04438' : undefined }}>
                {totalRows.toLocaleString()}
              </strong>
            </div>
          </div>

          {/* State-specific message */}
          {overLimit ? (
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                padding: '12px 14px',
                borderRadius: '8px',
                backgroundColor: 'rgba(240, 68, 56, 0.08)',
                border: '1px solid rgba(240, 68, 56, 0.35)',
              }}
            >
              <WarningAmberIcon
                style={{ color: '#f04438', fontSize: 20, flexShrink: 0 }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: '13px',
                  lineHeight: 1.5,
                  color: 'var(--text-primary-color)',
                }}
              >
                CSV exports are limited to{' '}
                <strong>{rowLimit.toLocaleString()}</strong> rows so they stay
                fast and reliable. This view has{' '}
                <strong>{totalRows.toLocaleString()}</strong> rows. Use the{' '}
                <strong>tabs</strong> (e.g. Flagged Lines) and the{' '}
                <strong>filters</strong> to narrow the results to{' '}
                {rowLimit.toLocaleString()} or fewer, then export again.
              </p>
            </div>
          ) : noRows ? (
            <p
              style={{
                margin: 0,
                fontSize: '13px',
                color: 'var(--text-secondary)',
              }}
            >
              There are no rows to export in this view. Adjust the tab or
              filters and try again.
            </p>
          ) : (
            <p
              style={{
                margin: 0,
                fontSize: '13px',
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
              }}
            >
              This will download <strong>{totalRows.toLocaleString()}</strong>{' '}
              row{totalRows === 1 ? '' : 's'} with the same columns as the
              table. The Flags column is expanded into one column per detected
              flag (Flag 1, Flag 2, &hellip;), each holding its full finding
              text.
            </p>
          )}

          {/* File name — optional. Blank falls back to defaultFileName. The
              .csv extension is appended by the exporter, so it's shown as a
              static suffix here rather than typed by the user. */}
          {!overLimit && !noRows && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              <label
                htmlFor='ace-export-file-name'
                style={{ fontSize: '13px', color: 'var(--text-secondary)' }}
              >
                File name <span style={{ opacity: 0.7 }}>(optional)</span>
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '0 10px',
                  borderRadius: '6px',
                  border: '1px solid var(--es-soft-border)',
                  backgroundColor: 'rgba(0, 0, 0, 0.15)',
                }}
              >
                <input
                  id='ace-export-file-name'
                  type='text'
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleExportClick();
                  }}
                  placeholder={defaultFileName}
                  disabled={isExporting}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--text-primary-color)',
                    fontSize: '14px',
                    padding: '10px 0',
                  }}
                />
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    flexShrink: 0,
                  }}
                >
                  .csv
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid var(--es-soft-border)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <button
            className='btn-outline'
            onClick={handleClose}
            disabled={isExporting}
            style={{ margin: 0, width: 'auto' }}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            disabled={!canExport}
            onClick={handleExportClick}
            style={{
              margin: 0,
              width: 'auto',
              opacity: canExport ? 1 : 0.5,
              cursor: canExport ? 'pointer' : 'not-allowed',
            }}
          >
            {isExporting ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              <>
                <IosShareIcon style={{ fontSize: 18 }} /> Download CSV
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AceTableExportModal;
